import { Alert } from "components/elements";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import * as services from "../service/pendingApprovalApi";
import { setIsLoading, setPendingWarningLetterList } from "../slices/requestApprovalSlice";
import { Modal } from "antd";
import { generateRandomString } from "utils/helper";

function useWarningLetter() {
    const { t, i18n } = useTranslation();

    function toParams(params = {}) {
        const { pagination = {}, sortBy = [] } = params;
        const modifiedParams = {
            pagination: {
                page: pagination.page,
                size: pagination.pageSize,
            },
            sortBy: sortBy
                .filter((s) => s.order !== undefined)
                .map((s) => ({
                    direction: s.order === "descend" ? "desc" : "asc",
                    property: s.field,
                })),
        };
        return modifiedParams;
    }

    const dispatch = useDispatch()
    const currentApprover = useSelector((state) => state.company.currentEmployee);
    const currentCompany = useSelector((state) => state.company.currentCompany);
    const [pagination, setPagination] = useState({ page: 0, size: 10 });
    const getWarningLetterRequest = useSelector((state) => state.requestApproval.pendingWarningLetterList)
    const [sorter, setSorter] = useState([]);


    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentWarningLetter, setCurrentWarningLetter] = useState({});
    const [employeeOptions, setEmployeeOptions] = useState([]);
    const [companyRegulationOptions, setCompanyRegulationOptions] = useState([]);

    const modifiedEmployeeOptions = employeeOptions?.map((opt) => ({
        ...opt,
        label: i18n.language === "en" ? opt?.fullNameEn : opt?.fullName,
        value: opt?.id,
    }));

    const modifiedCompanyRegulationOptions = companyRegulationOptions?.map((opt) => ({
        ...opt,
        label: i18n.language === "en" ? opt?.nameEn : opt?.name,
        value: opt?.id,
    }));

    const getEmployeeOptions = useCallback(async () => {
        try {
            const response = await services.getDropDown({ companyId: currentCompany });
            setEmployeeOptions(response?.data || []);
        } catch (error) {
            Alert({ type: "error", resultObject: error });
        }
    }, [currentCompany]);

    const getCompanyRegulationOptions = useCallback(async () => {
        if (!currentCompany) return;
        try {
            dispatch(setIsLoading(true));
            const values = {
                requestId: generateRandomString(),
                companyId: currentCompany,
            };
            const response = await services.getCompanyRegulationOptions(values);
            setCompanyRegulationOptions(response.data);
        } catch (error) {
            Alert({ type: "error", resultObject: error });
        } finally {
            dispatch(setIsLoading(false));
        }
    }, [currentCompany, dispatch]);

    const getPendingWarningLetterList = useCallback(
        async (params = {}) => {
            try {
                dispatch(setIsLoading(true));
                const response = await services.getPendingWarningLetterRequest({
                    ...pagination,
                    ...params,
                    companyId: currentCompany,
                    approverId: currentApprover,
                    pagingType: "pending"
                });
                dispatch(setPendingWarningLetterList(response.data?.content || []))
                setPagination({
                    page: params.page + 1,
                    size: params.size,
                    range: [
                        params.page * params.size + 1,
                        Math.min((params.page + 1) * params.size, response.data?.totalItems),
                    ],
                    total: response.data?.totalItems,
                });
            } catch (error) {
                Alert({ type: "error", resultObject: error });
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [currentCompany, currentApprover, pagination]
    );

    const onSearchWarning = useCallback(
        async (params = {}, values) => {
            try {
                dispatch(setIsLoading(true));
                const response = await services.getPendingWarningLetterRequest({
                    ...params,
                    ...pagination,
                    companyId: currentCompany,
                    approverId: currentApprover,
                    pagingType: "pending",
                    ...values,
                    page: 0, size: 50
                });
                dispatch(setPendingWarningLetterList(response.data?.content || []))
                setPagination({
                    page: params.page + 1,
                    size: params.size,
                    range: [
                        params.page * params.size + 1,
                        Math.min((params.page + 1) * params.size, response.data?.totalItems),
                    ],
                    total: response.data?.totalItems,
                });
            } catch (error) {
                Alert({ type: "error", resultObject: error });
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [currentCompany, currentApprover, pagination]
    );

    const onPageChange = useCallback(
        (tablePagination, _, tableSorter) => {
            setPagination(tablePagination);
            const modifiedSorter = Array.isArray(tableSorter)
                ? [...tableSorter]
                : [tableSorter];
            setSorter(modifiedSorter);
            const modifiedParams = toParams({
                pagination: {
                    page: tablePagination.current - 1,
                    pageSize: tablePagination.pageSize,
                },
                sortBy: modifiedSorter,
            });
            getPendingWarningLetterList(modifiedParams);
        },
        [getPendingWarningLetterList]
    )



    const onView = useCallback(async (record) => {
        try {
            dispatch(setIsLoading(true));
            const response = await services.getWarningLetterByIdRequest({
                companyId: currentCompany,
                employeeId: currentApprover,
                warningLetterRequestId: record.requestId,
                lang: i18n.language,
            });
            const data = response?.data || {};

            const getPositionName = (obj = {}) =>
                i18n.language === "en" ? obj?.positionNameEn : obj?.positionName;

            const mapped = {
                ...data,
                id: record.id,
                warningLetterRequestId: data.warningLetterRequestId,

                employeeId: data.warnedEmployee?.id,
                position: getPositionName(data.warnedEmployee),
                subject: data.companyRegulation?.id,
                subjectName:
                    i18n.language === "en"
                        ? data.companyRegulation?.nameEn
                        : data.companyRegulation?.name,

                incidentStartDate: data.incidentDate ? dayjs(data.incidentDate) : null,
                incidentEndDate: data.incidentEndDate ? dayjs(data.incidentEndDate) : null,
                issueDate: data.issueDate ? dayjs(data.issueDate) : null,

                reasons: data.reasons || [],
                otherReasonDetail: data.otherReasonDetail,
                incidentDetail: data.incidentDetail,
                effectOnCompany: data.effectOnCompany,

                authorizedSigner: {
                    employeeId: data.authorizedSigner?.id,
                    positionName: data.authorizedSigner?.positionName,
                    positionNameEn: data.authorizedSigner?.positionNameEn,
                    signDate: data.authorizedSignerSignDate ? dayjs(data.authorizedSignerSignDate) : null,
                    useIssueDateAsSignDate: data.useIssueDateAsSignDate,
                },
                supervisor: {
                    employeeId: data.supervisor?.id,
                    positionName: data.supervisor?.positionName,
                    positionNameEn: data.supervisor?.positionNameEn,
                    signDate: data.supervisorSignDate ? dayjs(data.supervisorSignDate) : null,
                    unspecified: data.supervisorUnspecified,
                },
                witness: {
                    employeeId: data.witness?.id,
                    positionName: data.witness?.positionName,
                    positionNameEn: data.witness?.positionNameEn,
                    signDate: data.witnessSignDate ? dayjs(data.witnessSignDate) : null,
                    unspecified: data.witnessUnspecified,
                },
                acknowledgedEmployee: {
                    receivedDate: data.acknowledgedReceivedDate ? dayjs(data.acknowledgedReceivedDate) : null,
                    notSignedYet: data.acknowledgedNotSignedYet,
                },

                startDate: data.startDate ? dayjs(data.startDate) : null,
                endDate: data.endDate ? dayjs(data.endDate) : null,
                createDate: data.createDate ? dayjs(data.createDate) : null,
                effectiveDate: data.effectiveDate ? dayjs(data.effectiveDate) : null,
            };


            setCurrentWarningLetter(mapped);
            setIsModalOpen(true);
        } catch (error) {
            Alert({ type: "error", resultObject: error });
        } finally {
            dispatch(setIsLoading(false));
        }
    }, [currentCompany, currentApprover, i18n.language]);

    const onCloseModal = useCallback(() => {
        setIsModalOpen(false);
        setCurrentWarningLetter({});
    }, []);

    const handleDelete = useCallback(
        async (record) => {
            try {
                dispatch(setIsLoading(true));

                const params = {
                    companyId: currentCompany,
                    employeeId: currentApprover,
                    sendingType: "CANCELLED",
                };

                let requestBody = {};
                requestBody = { ...params, warningLetterRequestId: record.requestId };
                await services.manageWarningLetterRequestStatus(requestBody);

                // Alert({ message: "Success" });
                const modifiedParams = toParams({
                    page: pagination.page - 1,
                    pageSize: pagination.size,
                });

                getPendingWarningLetterList(modifiedParams);
            } catch (error) {
                Alert({ type: "error", resultObject: error });
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [currentCompany, currentApprover, dispatch]
    );

    const onDelete = useCallback(
        (record) => {
            Modal.confirm({
                title: "Confirmation",
                content: (
                    <>
                        <p>Are you sure confirm to delete the data ?</p>
                    </>
                ),
                onOk() {
                    handleDelete(record);
                },
                okText: "CONFIRM",
                cancelText: "CANCEL",
            });
        },
        [handleDelete]
    );

    const handleApprove = useCallback(
        async (record) => {
            try {
                dispatch(setIsLoading(true));
                const params = {
                    companyId: currentCompany,
                    approverId: currentApprover,
                    sendingType: record.sendingType,
                };

                let requestBody = {};

                requestBody = { ...params, requestIds: [record.id] };
                await services.approveWarningLetterRequest(requestBody);

                // Alert({ message: "Success" });
                const modifiedParams = toParams({
                    page: pagination.page - 1,
                    pageSize: pagination.size,
                });
                getPendingWarningLetterList(modifiedParams);
                onCloseModal();
            } catch (error) {
                Alert({ type: "error", resultObject: error });
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [currentCompany, dispatch]
    );

    const onApprove = useCallback(
        (record) => {
            Modal.confirm({
                title: t('confirmation-1'),
                content: (
                    <>
                        <p>{t('warning_letter_confirm')}</p>
                    </>
                ),
                onOk() {
                    handleApprove({ ...record, sendingType: "APPROVED" });
                },
                okText: t("confirm"),
                cancelText: t("generalInfo.cancel"),
            });
        },
        []
    );

    const onReject = useCallback(
        (record) => {
            Modal.confirm({
                title: t('confirmation-1'),
                content: (
                    <>
                        <p>{t('warning_letter_reject')}</p>
                    </>
                ),
                onOk() {
                    handleApprove({ ...record, sendingType: "REJECT" });
                },
                okText: t("confirm"),
                cancelText: t("generalInfo.cancel"),
            });
        },
        []
    );

    const onCancel = useCallback(
        (record) => {
            Modal.confirm({
                title: "Confirmation",
                content: (
                    <>
                        <p>Are you sure confirm to delete the data ?</p>
                    </>
                ),
                onOk() {
                    handleApprove({ ...record, sendingType: "CANCELLED" });
                },
                okText: "CONFIRM",
                cancelText: "CANCEL",
            });
        },
        []
    );

    const onReturn = useCallback(
        (record) => {
            Modal.confirm({
                title: "Confirmation",
                content: (
                    <>
                        <p>Are you sure confirm to delete the data ?</p>
                    </>
                ),
                onOk() {
                    handleApprove({ ...record, sendingType: "RETURN" });
                },
                okText: "CONFIRM",
                cancelText: "CANCEL",
            });
        },
        []
    );

    const handleApproveAll = useCallback(
        async (selectedRecords) => {
            try {
                dispatch(setIsLoading(true));
                const requestBody = {
                    companyId: currentCompany,
                    approverId: currentApprover,
                    sendingType: "APPROVED",
                    requestIds: selectedRecords.map((record) => record.id),
                };
                await services.approveWarningLetterRequest(requestBody);
                // Alert({ message: "Success" });
                await getPendingWarningLetterList({ page: 0, size: 10 });
            } catch (error) {
                Alert({ type: "error", resultObject: error });
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [currentCompany, currentApprover, dispatch, getPendingWarningLetterList]
    );

    const handleRejectAll = useCallback(
        async (selectedRecords) => {
            try {
                dispatch(setIsLoading(true));
                const requestBody = {
                    companyId: currentCompany,
                    approverId: currentApprover,
                    sendingType: "REJECT",
                    requestIds: selectedRecords.map((record) => record.id),
                };
                await services.approveWarningLetterRequest(requestBody);
                // Alert({ message: "Success" });
                await getPendingWarningLetterList({ page: 0, size: 10 });
            } catch (error) {
                Alert({ type: "error", resultObject: error });
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [currentCompany, currentApprover, dispatch, getPendingWarningLetterList]
    );

    const onApproveAll = useCallback(
        (selectedRecords) => {
            return new Promise((resolve) => {
                const count = selectedRecords.length;

                Modal.confirm({
                    title: i18n.language === "en"
                        ? `Do you want to approve all ${count} selected requests ?`
                        : `คุณต้องการอนุมัติคำขอที่เลือก ทั้งหมด ${count} รายการหรือไม่ ?`,
                    content: (
                        <>
                            <p>{t('message.if_approved_cant_cancel')}</p>
                        </>
                    ),
                    onOk() {
                        return handleApproveAll(selectedRecords).then(resolve);
                    },
                    okText: t('common.confirm'),
                    cancelText: t('common.cancel'),
                    okButtonProps: { style: { height: "40px", borderRadius: "8px", backgroundColor: "var(--button-color)", boxShadow: "none" } },
                    cancelButtonProps: { style: { height: "40px", borderRadius: "8px", boxShadow: "none" } },
                });
            });
        },
        [handleApproveAll, i18n.language, t]
    );

    const onRejectAll = useCallback(
        (selectedRecords) => {
            return new Promise((resolve) => {
                const count = selectedRecords.length;

                Modal.confirm({
                    title: i18n.language === "en"
                        ? `Do you want to reject all ${count} selected requests ?`
                        : `คุณต้องการปฏิเสธคำขอที่เลือกทั้งหมด ${count} รายการหรือไม่ ?`,
                    content: (
                        <>
                            <p>{t('message.if_rejected_cant_cancel')}</p>
                        </>
                    ),
                    onOk() {
                        return handleRejectAll(selectedRecords).then(resolve);
                    },
                    okText: t('common.confirm'),
                    cancelText: t('common.cancel'),
                    okButtonProps: { style: { height: "40px", borderRadius: "8px", backgroundColor: "var(--button-color)", boxShadow: "none" } },
                    cancelButtonProps: { style: { height: "40px", borderRadius: "8px", boxShadow: "none" } },
                });
            });
        },
        [handleRejectAll, i18n.language, t]
    );

    useEffect(() => {
        if (currentCompany) {
            getCompanyRegulationOptions();
            getPendingWarningLetterList({ page: 0, size: 10 });
            getEmployeeOptions();
        }
    }, [currentCompany])

    return {
        onApprove,
        onCancel,
        onReject,
        onReturn,
        onApproveAll,
        onRejectAll,
        onView,
        isModalOpen,
        onCloseModal,
        currentWarningLetter,
        modifiedEmployeeOptions,
        modifiedCompanyRegulationOptions,
        onSearchWarning,
        getWarningLetterRequest,
        onchange: onPageChange
    }
}
export default useWarningLetter